import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import type Pubnub from 'pubnub';

import type { Person } from '@ninety/platform/models/_shared/person';
import type { Tokens } from '@ninety/platform/models/auth/refresh-token';
import type { BaseRealtimeMessage } from '@ninety/platform/models/meetings/realtime-message';
import type { AuthController } from '@ninety/platform/services/tokens/auth-controller.token';
import type { CurrentPersonController } from '@ninety/platform/services/tokens/current-person-controller.token';
import type { RealTimeController } from '@ninety/platform/services/tokens/real-time-controller.token';
import type { TeamListController } from '@ninety/platform/services/tokens/team-list-controller.token';

import { TeamListStateActions } from '../app-entities/team-list/team-list-state.actions';
import { AuthActions } from '../app-global/auth/auth.actions';
import { LogOutActions } from '../app-global/auth/logout/logout.actions';
import { CurrentPersonStateActions } from '../app-global/current-person/current-person.actions';
import { RealTimeActions } from '../app-global/real-time/real-time.actions';

/**
 * Concrete `platform/state` implementation of the command ("control") abstractions that services in
 * `platform/services` inject (DEV-26105). Counterpart to `PlatformServicesStateReader`: where that
 * exposes reads, this dispatches actions on the staying services' behalf so they no longer import
 * `platform/state` actions directly. The app composition root binds each control token to this class
 * via `useExisting`.
 */
@Injectable({ providedIn: 'root' })
export class PlatformServicesStateDispatcher
  implements RealTimeController, CurrentPersonController, TeamListController, AuthController
{
  private readonly store = inject(Store);

  updatePerson(person: Person): void {
    this.store.dispatch(CurrentPersonStateActions.updatePerson({ person }));
  }

  refreshTokenStart(): void {
    this.store.dispatch(AuthActions.refreshTokenStart());
  }

  setRefreshTokens(tokens: Tokens): void {
    this.store.dispatch(AuthActions.setRefreshTokens({ tokens }));
  }

  tokenRefreshed(token: string): void {
    this.store.dispatch(AuthActions.tokenRefreshed({ token }));
  }

  refreshTokenFail(): void {
    this.store.dispatch(AuthActions.refreshTokenFail());
  }

  initiateLogOut(): void {
    this.store.dispatch(LogOutActions.initiateLogOut());
  }

  changeFilterBarTeam(id: string): void {
    this.store.dispatch(TeamListStateActions.changeFilterBarTeam({ id }));
  }

  getSelectedTeam(id: string): void {
    this.store.dispatch(TeamListStateActions.getSelectedTeam({ id }));
  }

  addChannel(channelId: string, withPresence: boolean): void {
    this.store.dispatch(RealTimeActions.addChannel({ channelId, withPresence }));
  }

  removeChannel(channelId: string): void {
    this.store.dispatch(RealTimeActions.removeChannel({ channelId }));
  }

  messageReceived(payload: { channelId: string; message: BaseRealtimeMessage; emitterId?: string }): void {
    this.store.dispatch(RealTimeActions.messageReceived(payload));
  }

  presenceChanged(payload: { channelId: string; userId: string; action: Pubnub.PresenceEvent['action'] }): void {
    this.store.dispatch(RealTimeActions.presenceChanged(payload));
  }

  resetChannels(): void {
    this.store.dispatch(RealTimeActions.resetChannels());
  }
}
